'use client'

import { motion } from 'framer-motion'
import { ArrowRight, Check } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StarRating } from '@/components/ui/star-rating'

interface HeroProps {
  badge?: string
  badgeVariant?: 'primary' | 'ai'
  headline: string
  description: string
  primaryCTA: {
    text: string
    href: string
  }
  secondaryCTA?: {
    text: string
    href: string
  }
  rating?: {
    value: number
    reviews: number
    href: string
  }
  variant?: 'primary' | 'ai'
}

export function Hero({
  badge,
  badgeVariant = 'primary',
  headline,
  description,
  primaryCTA,
  secondaryCTA,
  rating,
  variant = 'primary',
}: HeroProps) {
  const isAi = variant === 'ai'

  return (
    <section
      className={`relative overflow-hidden py-20 md:py-28 lg:py-36 ${
        isAi
          ? 'bg-gradient-to-b from-secondary-50 to-white'
          : 'bg-gradient-to-b from-stone-50 to-white'
      }`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl opacity-50 ${
            isAi ? 'bg-secondary-100' : 'bg-primary-100'
          }`}
        />
        <div
          className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-50 ${
            isAi ? 'bg-primary-100' : 'bg-secondary-100'
          }`}
        />
      </div>

      <Container className="relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          {badge && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge variant={badgeVariant} className="mb-6">
                <Check className="w-3 h-3" />
                {badge}
              </Badge>
            </motion.div>
          )}

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-display-2 text-stone-900 mb-6"
          >
            {headline}
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-body-lg max-w-2xl mx-auto mb-10"
          >
            {description}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
          >
            <Button
              href={primaryCTA.href}
              external={primaryCTA.href.startsWith('http')}
              size="xl"
              variant={isAi ? 'ai' : 'primary'}
            >
              {primaryCTA.text}
              <ArrowRight className="w-5 h-5" />
            </Button>
            {secondaryCTA && (
              <Button
                href={secondaryCTA.href}
                variant="secondary"
                size="xl"
              >
                {secondaryCTA.text}
              </Button>
            )}
          </motion.div>

          {/* Rating */}
          {rating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex items-center justify-center gap-2"
            >
              <StarRating rating={rating.value} />
              <a
                href={rating.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-500 hover:text-stone-700 transition-colors"
              >
                {rating.value} stars ({rating.reviews} reviews)
              </a>
            </motion.div>
          )}
        </div>
      </Container>
    </section>
  )
}
